import {
    ApplicationHelpType,
    AssertionDescriptorUsageTypesEnum,
    AssertionDescriptors,
    CaseFileStaticItems,
    ConditionSelectorEnums,
    DataScopesEnum,
    QueryToolModeEnum,
    QueryTypes,
    SortDirections,
    TutorialTopics,
    UserRightsEnum,
    ZoneContextType,
} from '../../utilities/Constants';
import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
    processCriteria,
    processTriggerField,
    validateTrigger,
} from '../../components/triggers/trigger-details/validation';

import Authorization from '../../stores/Authorization';
import CaseHelper from '../../utilities/CaseHelper';
import { DocumentTitle } from '../../components/shared/DocumentTitle';
import { Help } from '../../components/shared/Help';
import { IAssertionDescriptorModel } from '../../interfaces/IAssertionDescriptor';
import { IQueryFilterCriteria } from '../../interfaces/IQuery';
import { IMultiSelectOption, IStaticItemLookupModel } from '../../interfaces/ILookup';
import { IQuadTriggerRuleSettingsModel, ITriggerRuleSettingsModel } from '../../interfaces/ITriggerRule';
import { IUserModel } from '../../interfaces/IUser';
import { IValidation } from '../../interfaces/IError';
import { LocalRoutes } from '../../utilities/LocalRoutes';
import { PageHeaderLayout } from '../../components/shared/PageHeaderLayout';
import { PageWithAffixedHeader } from '../../shared/PageWithAffixedHeader';
import { QueryTypeSelector } from '../query/QueryTypeSelector';
import Sort from '../../stores/Sort';
import { TriggerFieldValueControls } from '../../components/triggers/trigger-details/trigger-fields/TriggerFieldValueControls';
import { TriggerFieldsSection } from '../../components/triggers/trigger-details/trigger-fields/TriggerFieldsSection';
import { TriggerFilterCriteriaSection } from '../../components/triggers/trigger-details/filter-criteria/TriggerFilterCriteriaSection';
import { TriggerFilterCriteriaValueControls } from '../../components/triggers/trigger-details/filter-criteria/TriggerFilterCriteriaValueControls';
import UIHelper from '../../utilities/UIHelper';
import { cloneDeep } from 'lodash';
import { showErrorToast, showSuccessToast } from '../../shared/ToastUtils';
import { useAuthContext } from '../../contexts/AuthContext';
import { useCreateTriggerMutation } from '../../components/triggers/useCreateTriggerMutation';
import { useGetTriggerRule } from '../../components/triggers/trigger-details/useGetTriggerRule';
import { useLoading } from '../../contexts/LoadingContext';
import { useState } from 'react';
import { useUpdateTriggerMutation } from '../../components/triggers/useUpdateTriggerMutation';
import { useZoneContext } from '../../contexts/useZoneContext';
import { useZoneGetAssertionDescriptors } from '../../shared/react-query-hooks/useZoneGetAssertionDescriptors';
import { useZoneGetStaticItems } from '../../shared/react-query-hooks/useZoneGetStaticItems';
import CheckmateSelect from '../../components/shared/CheckmateSelect';
import CheckmateSelectHelper from '../../utilities/CheckmateSelectHelper';
import { SameCaseCaseFileModelFilter } from '../../components/shared/filters/SameCaseCaseFileModelFilter';
import { useLocation, useNavigate } from 'react-router-dom';

import {
    ISameCaseMatchedCasesSearchModel,
    SameCaseSortColumns,
    useGetSameCasesQuery,
} from '../quad/same-cases-admin/manage/useGetSameCasesQuery';
import { useCaseFileModelContext } from '../../contexts/useCaseFileModelContext';
import { useGetOrganizationByCaseModel } from '../../shared/react-query-hooks/useGetOrganizationByCaseModel';
import { useGetQuadAssertionDescriptorsByAuthorizedZones } from '../../shared/react-query-hooks/useQuadAssertionDescriptorsByAuthorizedZones';
import { useQuadCaseStaticItemsByAuthorizedZones } from '../../shared/react-query-hooks/useQuadCaseStaticItemsByAuthorizedZones';
import { useQuadCreateTriggerMutation } from '../../components/triggers/useQuadCreateTriggerMutation';

export interface ITriggerDetailsPageProps {
    isCreate?: boolean;
    isQuadScope?: boolean;
}

const defaultFilters = {
    pagedModel: {
        pageNumber: 1,
        pageSize: 10,
        rowsPerPage: 10,
    },
    sortModel: {
        sortColumn: SameCaseSortColumns.CaseName,//change to require 
        sortDirection: SortDirections.Asc,
    },
} as ISameCaseMatchedCasesSearchModel;

export function TriggerDetailsPage(props: ITriggerDetailsPageProps) {
    const navigate = useNavigate();
    const { triggerGuid } = useParams();
    const { user } = useAuthContext();
    const zoneContext = useZoneContext();
    const loading = useLoading();

    const loggedInUserDataScopeId = Authorization.getUserDefaultOrganizationDataScope(
        user,
        zoneContext.zoneGuid
    );
    const isLocalBasicUser = loggedInUserDataScopeId === DataScopesEnum.LocalBasic.Value;

    // used to create a ITriggerRuleSettingsModel on trigger Create for Local Basic Users
    const getDefaultFilterCriteria = useCallback(
        () => (): ITriggerRuleSettingsModel | undefined => {
            if (isLocalBasicUser) {
                return {
                    filters: [createLocalCounselFilter()],
                } as ITriggerRuleSettingsModel;
            }

            return undefined;
        },
        [isLocalBasicUser]
    );

    const createLocalCounselFilter = (): IQueryFilterCriteria => {
        return {
            staticItemId: CaseFileStaticItems.LocalCounsel.Id,
            readOnly: true,
            required: true,
            isFixed: true,
            label: CaseHelper.getCaseFileStaticItemDisplayName(
                staticItems ?? [],
                CaseFileStaticItems.LocalCounsel.Id
            ),
            value: UIHelper.getName(staticItems ?? [], CaseFileStaticItems.LocalCounsel.Id),
            selectedValues: [
                {
                    guid: user.homeOrganization?.guid ?? '',
                    label: user.homeOrganization?.name ?? '',
                    value: user.homeOrganization?.name ?? '',
                    isFixed: true,
                },
            ],
        };
    };

    const [validation, setValidation] = useState<IValidation>({});

    const createMutation = useCreateTriggerMutation((errorMessage: string) =>
        setValidation((prev) => ({ ...prev, create: [errorMessage] }))
    );

    const updateMutation = useUpdateTriggerMutation((errorMessage: string) =>
        setValidation((prev) => ({ ...prev, update: [errorMessage] }))
    );

    const quadcreateMutation = useQuadCreateTriggerMutation((errorMessage: string) =>
        setValidation((prev) => ({ ...prev, create: [errorMessage] }))
    );

    const {
        data,
        isError,
        error,
        isLoading,
        status: apiStatus,
    } = useGetTriggerRule(triggerGuid, true);

    const assertionDescriptorsQuery  = useZoneGetAssertionDescriptors(
        AssertionDescriptorUsageTypesEnum.Case,
        !props.isQuadScope //prevents service call
    );

    const staticItemsQuery = useZoneGetStaticItems(!props.isQuadScope);

    const queryTypes = CaseHelper.getAuthorizedQueryTypes(user, zoneContext.zoneGuid);
    const [queryTypeId, setQueryTypeId] = useState<number>(QueryTypes.CaseDetails.Value);

    const [currentFilters, setCurrentFilters] =
    useState<ISameCaseMatchedCasesSearchModel>(defaultFilters);
    const { caseFileModelId, userCaseFileModelsQuery } = props.isQuadScope
        ? useCaseFileModelContext()
        : { caseFileModelId: 0, userCaseFileModelsQuery: undefined };

    const organizationsWithAddTriggerRight = Authorization.getUserOrganizationsWithRight(
        UserRightsEnum.CaseTriggerRules,
        user
    );

    const organizationsByCaseModelQuery = props.isQuadScope
        ? useGetOrganizationByCaseModel(caseFileModelId)
        : { data: [], isLoading: false, isFetched: false };

    const filteredOrganizationsForCaseModel = useMemo(() => {
        if (!props.isQuadScope) return [];
        const modelOrgs = organizationsByCaseModelQuery.data ?? [];
        const allowedOrgs = organizationsWithAddTriggerRight ?? [];

        return modelOrgs.filter((org) => allowedOrgs.some((allowed) => allowed.guid === org.guid));
    }, [
        organizationsByCaseModelQuery.data,
        organizationsWithAddTriggerRight,
        props.isQuadScope,
    ]);

    const selectedOrganizationGuids = useMemo(
        () => CheckmateSelectHelper.getOrgGuids(filteredOrganizationsForCaseModel),
        [filteredOrganizationsForCaseModel]
    );

    const hasValidQuadOrgs = props.isQuadScope && selectedOrganizationGuids.length > 0;

    const quadAssertionDescriptorsQuery = useGetQuadAssertionDescriptorsByAuthorizedZones(
        caseFileModelId,
        selectedOrganizationGuids,
        hasValidQuadOrgs 
    );

    const quadStaticItemsQuery = useQuadCaseStaticItemsByAuthorizedZones(
        caseFileModelId,
        selectedOrganizationGuids,
        hasValidQuadOrgs 
    );

    const assertionDescriptors: IAssertionDescriptorModel[] =
        props.isQuadScope
            ? quadAssertionDescriptorsQuery.data ?? []
            : assertionDescriptorsQuery.data ?? [];

    const staticItems: IStaticItemLookupModel[] =
        props.isQuadScope
            ? quadStaticItemsQuery.data ?? []
            : staticItemsQuery.data ?? [];

    const [localTrigger, setLocalTrigger] = useState<ITriggerRuleSettingsModel | undefined>(() => {
        const base = (data ?? getDefaultFilterCriteria()) as ITriggerRuleSettingsModel | undefined;

        if (props.isQuadScope) {
            return {
                ...(base ?? ({} as ITriggerRuleSettingsModel)),
                selectedOrganizations: base?.selectedOrganizations ?? [],
            };
        }

        return base;
    });

    const [suppressDateRangeConditionCondition, setsuppressDateRangeCondition] =
        useState<boolean>(true);

    const processCaseStatusRangeRule = (criteria: IQueryFilterCriteria[]) => {
        const foundCaseStatus = criteria.find(
            (crit) => crit.staticItemId === CaseFileStaticItems.CaseStatus.Id
        );

        const handleHideDateRangeCondition = () => {
            const localTriggerCopy = cloneDeep(localTrigger);
            localTriggerCopy?.filters
                ?.filter((criteria) => criteria.condition === ConditionSelectorEnums.Range)
                .forEach((criteria) => {
                    criteria.condition = ConditionSelectorEnums.EqualTo;
                    criteria.dateRangeInterval = undefined;
                });

            localTriggerCopy?.triggerFields
                ?.filter((rule) => rule.condition === ConditionSelectorEnums.Range)
                .forEach((rule) => {
                    rule.condition = ConditionSelectorEnums.EqualTo;
                    rule.dateRangeInterval = undefined;
                });

            setLocalTrigger(localTriggerCopy);
            setsuppressDateRangeCondition(true);
        };

        let hide = true;

        if (foundCaseStatus) {
            const equalTo = foundCaseStatus.condition === ConditionSelectorEnums.EqualTo;
            const notEqualTo = foundCaseStatus.condition === ConditionSelectorEnums.NotEqualTo;
            const selectedIds = foundCaseStatus.selectedValues?.map((obj) => obj.id) ?? [];

            if (equalTo) {
                const ongoingStatuses = [1, 2, 6, 8];
                const allFromOngoing = selectedIds
                    .filter((id) => !!id)
                    .every((id) => ongoingStatuses.includes(id!));

                hide = !(selectedIds.length > 0 && allFromOngoing);
            } else if (notEqualTo) {
                const closedStatuses = [3, 4, 5, 7, 9, 10, 11];
                const allClosedPresent = closedStatuses.every((status) =>
                    selectedIds.includes(status)
                );

                const noExtraValues = selectedIds.length === closedStatuses.length;

                hide = !(allClosedPresent && noExtraValues);
            }
        }

        if (hide) {
            handleHideDateRangeCondition();
        }

        setsuppressDateRangeCondition(hide);
    };

    const findCriteriaItem = (
        localTrigger: ITriggerRuleSettingsModel | undefined,
        item: IQueryFilterCriteria
    ) => {
        return localTrigger?.filters?.find((criteria) =>
            criteria.isAssertionDescriptor
                ? criteria.assertionDescriptorGuid === item.assertionDescriptorGuid
                : criteria.staticItemId === item.staticItemId
        );
    };

    const findTriggerFieldItem = (
        localTrigger: ITriggerRuleSettingsModel | undefined,
        item: IQueryFilterCriteria
    ) => {
        return localTrigger?.triggerFields?.find((field) => {
            if (field.isAssertionDescriptor) {
                return field.assertionDescriptorGuid === item.assertionDescriptorGuid;
            } else {
                return field.staticItemId === item.staticItemId;
            }
        });
    };

    const handleSaveClick = () => {
        const queryTypeLU = queryTypes.find((qt) => qt.id === queryTypeId);
        const localTriggerCopy = cloneDeep({
            ...localTrigger,
            queryType: queryTypeLU,
        }) as ITriggerRuleSettingsModel;

        const validation = validateTrigger(
            props.isQuadScope ?? false,
            localTriggerCopy,
            assertionDescriptors ?? [],
            (validation: IValidation) => setValidation(validation)
        );

        if (!validation) {
            setLocalTrigger(localTriggerCopy);
            return false;
        }

        loading.showLoading();

        if(props.isQuadScope){
            const quadTriggerPayload: IQuadTriggerRuleSettingsModel = {
                triggerRuleSettings: localTriggerCopy,
                organizations: localTriggerCopy.selectedOrganizations, // if you still capture orgs here before full cleanup
            };
             quadcreateMutation
                .mutateAsync(quadTriggerPayload)
                .then((newTrigger) => {
                    if (newTrigger) {
                        showSuccessToast(<span>Successfully created trigger rule(s) for selected organization(s)!</span>);
                         setTimeout(() => {
                            navigate(LocalRoutes.QuadTriggerRuleCreate);
                        }, 2000); // 2000ms = 2 seconds
                    }
                })
                .catch((error) => {
                    showErrorToast('Failed to create trigger rule');
                })
                .finally(() => {
                    loading.hideLoading();
                });
        }                
        else if (props.isCreate) {
            createMutation
                .mutateAsync(localTriggerCopy)
                .then((newTrigger) => {
                    if (newTrigger) {
                        zoneContext.zoneNavigate(LocalRoutes.Triggers);
                    }
                })
                .catch((error) => {
                    showErrorToast('Failed to create trigger rule');
                })
                .finally(() => {
                    loading.hideLoading();
                });
        } else {
            updateMutation
                .mutateAsync(localTriggerCopy)
                .then((updatedTrigger) => {
                    if (updatedTrigger) {
                        zoneContext.zoneNavigate(LocalRoutes.Triggers);
                    }
                })
                .catch((error) => {
                    showErrorToast('Failed to update trigger rule');
                })
                .finally(() => {
                    loading.hideLoading();
                });
        }
    };

    const handleCancelClick = () => {
        zoneContext.zoneNavigate(LocalRoutes.Triggers);
    };

    const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        setLocalTrigger(
            (prev) => ({ ...prev, ruleName: e.target.value }) as ITriggerRuleSettingsModel
        );
    };

    const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setLocalTrigger(
            (prev) => ({ ...prev, ruleDescription: e.target.value }) as ITriggerRuleSettingsModel
        );
    };

    const handleQueryTypeChange = (selected: any) => {
        setQueryTypeId(selected?.id);
    };

    const handleSelectedQueryFilterCriteriaChange = (selectedCriteria: IQueryFilterCriteria[]) => {
        setLocalTrigger((prev) => {
            const selectedCriteriaCopy = cloneDeep(selectedCriteria);

            if (isLocalBasicUser) {
                const existingLocalCounselFilter = prev?.filters.find(
                    (filter) => filter.staticItemId === CaseFileStaticItems.LocalCounsel.Id
                );
                const localCounselFilter = createLocalCounselFilter();

                if (!existingLocalCounselFilter) {
                    selectedCriteriaCopy.unshift(localCounselFilter);
                }
            }

            if (prev) {
                return { ...prev, filters: selectedCriteriaCopy };
            } else {
                return { filters: selectedCriteriaCopy } as ITriggerRuleSettingsModel;
            }
        });
    };

    const handleCriteriaConditionChange = (item: IQueryFilterCriteria, itemIndex: number) => {
        if (!localTrigger) {
            return;
        }
        const tirggerCopy = cloneDeep(localTrigger);
        item.validationError = '';
        tirggerCopy.filters[itemIndex] = item;
        setLocalTrigger(tirggerCopy);
    };

    const handleSelectedTriggerFieldsChange = (fields: IQueryFilterCriteria[]) => {
        const triggerFields = fields.map((field) => {
            if (!field.condition) {
                if(props.isQuadScope){
                    field.condition = ConditionSelectorEnums.AllChanges; 
                }
                else
                    field.condition = 'Equal To';
            }
            return field;
        });
        setLocalTrigger((prev) => {
            if (prev) {
                return { ...prev, triggerFields };
            } else {
                return { triggerFields: fields } as ITriggerRuleSettingsModel;
            }
        });
    };

    const handleCriteriaSelectorChange = (item: IQueryFilterCriteria, options: any) => {
        const localTriggerCopy = cloneDeep(localTrigger);
        const found = findCriteriaItem(localTriggerCopy, item);

        if (found) {
            found.selectedValues = options;
            processCriteria(found, assertionDescriptors ?? []);
            setLocalTrigger(localTriggerCopy);
        }
    };

    const handleTriggerOrganizationsSelected = (selectedOrgs: IMultiSelectOption[]) => {
        setLocalTrigger((prev) => {
            const base = (prev ?? {}) as ITriggerRuleSettingsModel;
            return { ...base, selectedOrganizations: selectedOrgs };
        });
    };

    const handleCriteriaTextChange = (item: IQueryFilterCriteria, value: string) => {
        const localTriggerCopy = cloneDeep(localTrigger);
        const found = findCriteriaItem(localTriggerCopy, item);

        if (found) {
            found.text = value;
            processCriteria(found, assertionDescriptors ?? []);
            setLocalTrigger(localTriggerCopy);
        }
    };

    const handleCriteriaYesNoChange = (item: IQueryFilterCriteria, optionalBool: any) => {
        const localTriggerCopy = cloneDeep(localTrigger);
        const found = findCriteriaItem(localTriggerCopy, item);

        if (found) {
            found.booleanValue = optionalBool;
            processCriteria(found, assertionDescriptors ?? []);
            setLocalTrigger(localTriggerCopy);
        }
    };

    const handleCriteriaDateRangeChange = (
        item: IQueryFilterCriteria,
        field: string,
        value: string
    ) => {
        const localTriggerCopy = cloneDeep(localTrigger);
        const found = findCriteriaItem(localTriggerCopy, item);

        if (found) {
            if (field === 'startDate') {
                found.startDate = value;
            } else if (field === 'endDate') {
                found.endDate = value;
            }
            processCriteria(found, assertionDescriptors ?? []);
            setLocalTrigger(localTriggerCopy);
        }
    };

    const handleCriteriaRelativeDateChange = (item: IQueryFilterCriteria, value: number) => {
        const localTriggerCopy = cloneDeep(localTrigger);
        const found = findCriteriaItem(localTriggerCopy, item);

        if (found) {
            found.dateRangeInterval = value;
            found.startDate = undefined;
            found.endDate = undefined;
            found.validationError = '';
            processCriteria(found, assertionDescriptors ?? []);
            setLocalTrigger(localTriggerCopy);
        }
    };

    const handleCriteriaNumericRangeChange = (
        item: IQueryFilterCriteria,
        field: string,
        value: number | string | undefined
    ) => {
        const localTriggerCopy = cloneDeep(localTrigger);
        const found = findCriteriaItem(localTriggerCopy, item);

        if (found) {
            if (field === 'from') {
                found.numericValueFromText = value ? value.toString() : '';
                found.numericValueFrom = Number(value);
            } else if (field === 'to') {
                found.numericValueToText = value ? value.toString() : '';
                found.numericValueTo = Number(value);
            }
            processCriteria(found, assertionDescriptors ?? []);
            setLocalTrigger(localTriggerCopy);
        }
    };

    const handleTriggerFieldConditionChange = (item: IQueryFilterCriteria, itemIndex: number) => {
        if (!localTrigger) {
            return;
        }
        const tirggerCopy = cloneDeep(localTrigger);

        item.validationError = '';
        tirggerCopy.triggerFields[itemIndex] = item;

        setLocalTrigger(tirggerCopy);
    };

    const handleTriggerFieldSelectorChange = (item: IQueryFilterCriteria, options: any) => {
        const localTriggerCopy = cloneDeep(localTrigger);
        const found = findTriggerFieldItem(localTriggerCopy, item);

        if (found) {
            found.selectedValues = options;
            processTriggerField(found, assertionDescriptors ?? []);
            setLocalTrigger(localTriggerCopy);
        }
    };

    const handleTriggerFieldTextChange = (item: IQueryFilterCriteria, value: string) => {
        const localTriggerCopy = cloneDeep(localTrigger);
        const found = findTriggerFieldItem(localTriggerCopy, item);

        if (found) {
            found.text = value;
            processTriggerField(found, assertionDescriptors ?? []);
            setLocalTrigger(localTriggerCopy);
        }
    };

    const handleTriggerFieldYesNoChange = (item: IQueryFilterCriteria, optionalBool: any) => {
        const localTriggerCopy = cloneDeep(localTrigger);
        const found = findTriggerFieldItem(localTriggerCopy, item);

        if (found) {
            found.booleanValue = optionalBool;
            processTriggerField(found, assertionDescriptors ?? []);
            setLocalTrigger(localTriggerCopy);
        }
    };

    const handleTriggerFieldDateRangeChange = (
        item: IQueryFilterCriteria,
        field: string,
        value: string
    ) => {
        const localTriggerCopy = cloneDeep(localTrigger);
        const found = findTriggerFieldItem(localTriggerCopy, item);

        if (found) {
            if (field === 'startDate') {
                found.startDate = value;
            } else if (field === 'endDate') {
                found.endDate = value;
            }
            processTriggerField(found, assertionDescriptors ?? []);
            setLocalTrigger(localTriggerCopy);
        }
    };

    const handleTriggerFieldRelativeDateChange = (
        item: IQueryFilterCriteria,
        value: number | undefined
    ) => {
        const localTriggerCopy = cloneDeep(localTrigger);
        const found = findTriggerFieldItem(localTriggerCopy, item);

        if (found) {
            found.dateRangeInterval = value;
            found.startDate = undefined;
            found.endDate = undefined;
            found.validationError = '';
            processCriteria(found, assertionDescriptors ?? []);
            setLocalTrigger(localTriggerCopy);
        }
    };

    const handleTriggerFieldNumericRangeChange = (
        item: IQueryFilterCriteria,
        field: string,
        value: number | string | undefined
    ) => {
        const localTriggerCopy = cloneDeep(localTrigger);
        const found = findTriggerFieldItem(localTriggerCopy, item);

        if (found) {
            if (field === 'from') {
                found.numericValueFromText = value ? value.toString() : '';
                found.numericValueFrom = Number(value);
            } else if (field === 'to') {
                found.numericValueToText = value ? value.toString() : '';
                found.numericValueTo = Number(value);
            }
            processTriggerField(found, assertionDescriptors ?? []);
            setLocalTrigger(localTriggerCopy);
        }
    };

    const availableFilterCriterias = getAvailableFilterCriterias(
        assertionDescriptors ?? [],
        staticItems ?? [],
        user,
        zoneContext
    );

    const availableTriggerFieldCriterias = [...availableFilterCriterias];

    const filteredFilterCriteria = props.isQuadScope
                ? availableFilterCriterias.filter(
                        (crit) =>
                        crit.staticItemId === CaseFileStaticItems.CaseManager.Id ||
                        crit.staticItemId === CaseFileStaticItems.LocalCounsel.Id)
                : availableFilterCriterias
                    .filter((crit) => !localTrigger?.triggerFields?.some((tr) => tr.value === crit.value))
                    .filter(
                        (crit) =>
                            !isLocalBasicUser ||
                            (isLocalBasicUser && crit.staticItemId !== CaseFileStaticItems.LocalCounsel.Id)
                    );

    const filteredTriggerFieldCriteria = availableTriggerFieldCriterias.filter(
        (crit) => !localTrigger?.filters?.some((f) => f.value === crit.value)
    );

    const showFilterCriteria = useMemo(
        () =>
            [
                QueryTypes.CaseDetails.Value,
                QueryTypes.CaseExperts.Value,
                QueryTypes.CaseNotes.Value,
                QueryTypes.Deposition.Value,
            ].includes(queryTypeId),
        [queryTypeId]
    );

    const initializeNumericTextFields = (
        trigger: ITriggerRuleSettingsModel | undefined
    ): ITriggerRuleSettingsModel | undefined => {
        if (!trigger) return trigger;

        const updatedTrigger = cloneDeep(trigger);

        updatedTrigger.filters?.forEach((filter) => {
            if (
                filter.numericValueFrom &&
                filter.numericValueFrom != null &&
                filter.numericValueFromText === undefined
            ) {
                filter.numericValueFromText = filter.numericValueFrom?.toString() ?? '';
            }
            if (
                filter.numericValueTo &&
                filter.numericValueTo != null &&
                filter.numericValueToText === undefined
            ) {
                filter.numericValueToText = filter.numericValueTo.toString() ?? '';
            }
        });

        updatedTrigger.triggerFields?.forEach((field) => {
            if (
                field.numericValueFrom &&
                field.numericValueFrom != null &&
                field.numericValueFromText === undefined
            ) {
                field.numericValueFromText = field.numericValueFrom?.toString() ?? '';
            }
            if (
                field.numericValueTo &&
                field.numericValueTo != null &&
                field.numericValueToText === undefined
            ) {
                field.numericValueToText = field.numericValueTo?.toString() ?? '';
            }
        });

        return updatedTrigger;
    };
    const showTriggerFields = useMemo(() => true, [queryTypeId]);
    const pageTitle = props.isQuadScope? 'Quad - Trigger Rules' : props.isCreate ? 'New Trigger Rule' : 'Trigger Rule';
    const saveButtonText = props.isCreate ? 'Create' : 'Save';

    useEffect(() => {
        if (apiStatus === 'success' && !staticItems) {
            const triggerCopy = cloneDeep(data);

            // preserve zones that user already selected in UI (create page / quad editor)
            const existingSelectedOrgs = localTrigger?.selectedOrganizations ?? [];

            // force a criteria by Local Counsel if user is Local Basic
            if (isLocalBasicUser && user.homeOrganization && triggerCopy) {
                // removed any saved Local Counsel Filter
                const filters =
                    triggerCopy?.filters.filter(
                        (filter) => filter.staticItemId !== CaseFileStaticItems.LocalCounsel.Id
                    ) ?? [];

                // add readonly local counsel filter
                filters.unshift(createLocalCounselFilter());
                triggerCopy.filters = filters;
            }

            const initializedTrigger = initializeNumericTextFields(triggerCopy);
            if (props.isQuadScope) {
                setLocalTrigger({
                    ...(initializedTrigger ?? ({} as ITriggerRuleSettingsModel)),
                    selectedOrganizations:
                        (initializedTrigger?.selectedOrganizations?.length ?? 0) > 0
                            ? initializedTrigger!.selectedOrganizations
                            : existingSelectedOrgs,
                });
            }
            else
                setLocalTrigger(initializedTrigger);
        }
    }, [apiStatus, data, staticItems]);

    useEffect(() => {
        if (!loading && props.isCreate) {
            handleSelectedQueryFilterCriteriaChange([]);
        }
    }, [loading]);

    //update zones/selected zones based on the casefile modelid.
    useEffect(() => {
        if (!props.isQuadScope || !localTrigger) return;

        setLocalTrigger((prev) => {
            if (!prev?.selectedOrganizations?.length) return prev;

            const allowedGuids = new Set(
                filteredOrganizationsForCaseModel.map((o) => o.guid)
            );

            const filteredSelected = prev.selectedOrganizations.filter(
                (org) => allowedGuids.has(org.guid)
            );

            // Avoid unnecessary state update
            if (filteredSelected.length === prev.selectedOrganizations.length) {
                return prev;
            }

            return {
                ...prev,
                selectedOrganizations: filteredSelected,
            };
        });
    }, [filteredOrganizationsForCaseModel, props.isQuadScope]);


   const isComponentLoading = useMemo(() => {
       if ( props.isQuadScope && (!filteredOrganizationsForCaseModel || filteredOrganizationsForCaseModel.length === 0)) {
           return true;
       }
       return props.isQuadScope
           ? quadAssertionDescriptorsQuery.isLoading || quadStaticItemsQuery.isLoading || isLoading
           : assertionDescriptorsQuery.isLoading || staticItemsQuery.isLoading || isLoading;
   }, [
       props.isQuadScope,
       quadAssertionDescriptorsQuery.isLoading,
       quadStaticItemsQuery.isLoading,
       assertionDescriptorsQuery.isLoading,
       staticItemsQuery.isLoading,
       isLoading,
       filteredOrganizationsForCaseModel, // add this to dependency
   ]);


    useEffect(() => {
        if (isComponentLoading) {
            loading.showLoading();
        } else {
            loading.hideLoading();
        }
    }, [isComponentLoading]);


    useEffect(() => {
        if (!props.isQuadScope) {
            processCaseStatusRangeRule(localTrigger?.filters ?? []);
        }
    }, [JSON.stringify(localTrigger?.filters), JSON.stringify(localTrigger?.triggerFields)]);

    useEffect(() => {
        setCurrentFilters({ ...defaultFilters, caseFileModelId });
    }, [caseFileModelId]);

    if (!isLoading && isError) {
        return (
            <div className="row">
                <div className="text-danger">Failed to load trigger rule.</div>
            </div>
        );
    }

    return (
        <PageWithAffixedHeader
            headerComponent={
                <PageHeaderLayout
                    title={pageTitle}
                    actions={
                        <>
                            <Link
                                target="_blank"
                                to={`${LocalRoutes.ViewSpecificPDF.replace(
                                    ':id',
                                    TutorialTopics.Triggers.toString()
                                )}`}
                            >
                                <span className="btn btn-no-bg text-gray">
                                    <i className={'fal fa-graduation-cap'} />
                                    &nbsp;Tutorials
                                </span>
                            </Link>
                            <>
                                <button className="btn btn-orange" onClick={handleSaveClick}>
                                    {saveButtonText}
                                </button>
                                <button className="btn btn-default" onClick={handleCancelClick}>
                                    Cancel
                                </button>
                            </>
                        </>
                    }
                    disableBottomPadding
                />
            }
        >
            <DocumentTitle title={localTrigger?.ruleName || 'Unnamed Trigger Rule'} />
 
            <div className="row mb-2">
                <div className="col-sm-2"></div>
                <div className="col-sm-10">
                    {isError && <div className="text-danger">{error as string}</div>}
                    <div className="text-danger">{validation.model}</div>
                    <div className="text-danger">{validation.create}</div>
                    <div className="text-danger">{validation.update}</div>
                </div>
            </div>
            {props.isQuadScope && (
                <>
                    <div className="row mb-2">
                        <div className="col-sm-2">{/* Label / empty space */}</div>

                        <div className="col-sm-10">
                            <div className="row">
                                <div className="col-sm-4">
                                    <SameCaseCaseFileModelFilter
                                        currentFilters={currentFilters}
                                        setCurrentFilters={setCurrentFilters}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row mb-2">
                        <div className="col-sm-2">
                            <label className="text-gray">Zones *</label>
                        </div>
                        <div className="col-sm-10">
                            <div className="col-sm-3">
                                <CheckmateSelect
                                    isMulti={true}
                                    options={CheckmateSelectHelper.getOrgOptions(
                                        filteredOrganizationsForCaseModel
                                    )}
                                    value={localTrigger?.selectedOrganizations ?? []}
                                    onChange={handleTriggerOrganizationsSelected}
                                />
                                <span className="text-danger">{validation.organizationGuids}</span>
                            </div>
                        </div>
                    </div>
                </>
            )}
            <QueryTypeSelector
                helpComponent={
                    <Help
                        type={ApplicationHelpType.Info}
                        title="Type"
                        helpText="Type is a fixed field for Trigger Rules and cannot be modified"
                    />
                }
                mode={QueryToolModeEnum.Standalone}
                onChange={handleQueryTypeChange}
                readonly
                selectorLabel="Type"
                selectedQueryType={queryTypeId}
                user={user}
                validation={validation}
                zoneContext={zoneContext}
            />
            <div className="row mb-2">
                <div className="col-sm-2">
                    <label className="text-gray text-lg">Name*</label>
                </div>
                <div className="col-sm-3">
                    <input
                        type="text"
                        className="form-control"
                        value={localTrigger?.ruleName ?? ''}
                        onChange={handleNameChange}
                        style={{ width: '100%' }}
                    />
                    <span className="text-danger">{validation.ruleName}</span>
                </div>
            </div>
            <div className="row mb-2">
                <div className="col-sm-2">
                    <label className="text-gray text-lg">Description</label>
                </div>
                <div className="col-sm-10">
                    <textarea
                        className="form-control"
                        value={localTrigger?.ruleDescription}
                        onChange={handleDescriptionChange}
                        rows={3}
                    />
                </div>
                <span className="text-danger">{validation.ruleDescription}</span>
            </div>
            {showFilterCriteria && (
                    <TriggerFilterCriteriaSection
                            isQuadScope={props.isQuadScope}
                            availableFilterCriterias={filteredFilterCriteria}
                            buildValueControls={(item) => (
                                    <TriggerFilterCriteriaValueControls
                                        item={item}
                                        onDateRangeChange={handleCriteriaDateRangeChange}
                                        onRelativeDateChange={handleCriteriaRelativeDateChange}
                                        onNumericRangeChange={handleCriteriaNumericRangeChange}
                                        onSelectorChange={handleCriteriaSelectorChange}
                                        onTextChange={handleCriteriaTextChange}
                                        onYesNoChange={handleCriteriaYesNoChange}
                                    />
                                )
                            }
                            onSelectedQueryFilterCriteriaChange={handleSelectedQueryFilterCriteriaChange}
                            onConditionOptionChange={handleCriteriaConditionChange}
                            queryType={queryTypeId}
                            selectedQueryFilterCriteria={localTrigger?.filters ?? []}
                            suppressDateRangeCondition={suppressDateRangeConditionCondition}
                            user={user}
                            validation={validation}
                        />
            )}
            {showTriggerFields && (
                <TriggerFieldsSection
                    availableFilterCriterias={filteredTriggerFieldCriteria}
                    buildValueControls={(item) =>
                        props.isQuadScope ? (
                            <></>
                        ) : (
                            <TriggerFieldValueControls
                                item={item}
                                onDateRangeChange={handleTriggerFieldDateRangeChange}
                                onRelativeDateChange={handleTriggerFieldRelativeDateChange}
                                onNumericRangeChange={handleTriggerFieldNumericRangeChange}
                                onSelectorChange={handleTriggerFieldSelectorChange}
                                onTextChange={handleTriggerFieldTextChange}
                                onYesNoChange={handleTriggerFieldYesNoChange}
                            />
                        )
                    }
                    onConditionOptionChange={handleTriggerFieldConditionChange}
                    onSelectedTriggerFieldsChange={handleSelectedTriggerFieldsChange}
                    queryType={queryTypeId}
                    selectedTriggerFields={localTrigger?.triggerFields ?? []}
                    suppressDateRangeCondition={suppressDateRangeConditionCondition}
                    user={user}
                    validation={validation}
                    isQuadScope={props.isQuadScope}
                    assertionDescriptors ={assertionDescriptors}
                />
            )}
        </PageWithAffixedHeader>
    );
}

const getAvailableFilterCriterias = (
    assertionDescriptors: IAssertionDescriptorModel[],
    staticItems: IStaticItemLookupModel[],
    user: IUserModel,
    zoneContext: ZoneContextType
): IQueryFilterCriteria[] => {
    const packageDealAuthFilter = (staticItem: IStaticItemLookupModel) => {
        const isPackageDeal = staticItem.guid === CaseFileStaticItems.PackageSettlement.Guid;
        return (
            !isPackageDeal ||
            (isPackageDeal &&
                Authorization.userHasRight(
                    UserRightsEnum.ViewPackageSettlement,
                    user,
                    zoneContext.zoneGuid
                ))
        );
    };

    const staticSectionFilter = (staticItem: IStaticItemLookupModel) =>
        ![
            CaseFileStaticItems.ExpertsSection.Id,
            CaseFileStaticItems.DefenseCounselSection.Id,
            CaseFileStaticItems.DepositionSection.Id,
            CaseFileStaticItems.NotesSection.Id,
            CaseFileStaticItems.PackageSettlement.Id,
            CaseFileStaticItems.CaseStatusAsOfDate.Id,
            CaseFileStaticItems.ResolutionDate.Id,
            CaseFileStaticItems.AssessmentPredictedOutcomeAmount.Id,
            CaseFileStaticItems.AssessmentThreatScore.Id,
        ].includes(staticItem.id);

    const staticItemToQueryFilterCriteriaMapper = (staticItem: IStaticItemLookupModel) =>
        ({
            label: CaseHelper.getCaseFileStaticItemDisplayName(staticItems, staticItem.id),
            value: UIHelper.getName(staticItems, staticItem.id),
            isAssertionDescriptor: false,
            staticItemId: staticItem.id,
        }) as IQueryFilterCriteria;

    const caseAlternateManagerFilter = (item: IStaticItemLookupModel) =>
        item.id !== CaseFileStaticItems.AlternateCaseManager.Id;

    const caseManagerMapper = (item: IQueryFilterCriteria) => {
        if (item.staticItemId === CaseFileStaticItems.CaseManager.Id) {
            return { ...item, label: 'A1/A2' };
        }
        return item;
    };

    const staticItemOptions = staticItems
        .filter(packageDealAuthFilter)
        .filter(staticSectionFilter)
        .filter(caseAlternateManagerFilter)
        .map(staticItemToQueryFilterCriteriaMapper)
        .map(caseManagerMapper);

    const nationalPlaintiffsFirmAdFilter = (item: IAssertionDescriptorModel) =>
        item.guid !== AssertionDescriptors.PlainfirmFirmSecondary.Guid.toLowerCase();

    const otherOccupationAdFilter = (item: IAssertionDescriptorModel) =>
        item.guid !== AssertionDescriptors.OtherOccupation.Guid.toLowerCase();

    const adOptions = assertionDescriptors
        .filter((x) => x.usageType && x.usageType.id === AssertionDescriptorUsageTypesEnum.Case)
        .filter(nationalPlaintiffsFirmAdFilter)
        .filter(otherOccupationAdFilter)
        .map(
            (item: IAssertionDescriptorModel) =>
                ({
                    label: item.alternateDisplayName || item.displayName!,
                    value: item.name!,
                    isAssertionDescriptor: true,
                    assertionDescriptorGuid: item.guid,
                }) as IQueryFilterCriteria
        );

    return staticItemOptions.concat(adOptions).sort(Sort.compareValues('label'));
};

